import React, { useState, useEffect, useCallback, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AMMONIA_THRESHOLDS,
  GROWTH_THRESHOLDS,
  WET_THRESHOLDS,
  getStatus,
} from './NotificationProvider';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

// ─── Orientation → CSS transform map ─────────────────────────────────────────

const ORIENTATION_TRANSFORMS = {
  'Default':     'none',
  'Rotate 90°':  'rotate(90deg)',
  'Rotate 180°': 'rotate(180deg)',
  'Rotate 270°': 'rotate(270deg)',
  'Flip H':      'scaleX(-1)',
  'Flip V':      'scaleY(-1)',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getReadingScore = (reading, index) => {
  const timestamp = reading?.iso8601_timestamp_string
    ?.replace(' ', 'T')
    .replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
  const parsedTime = timestamp ? new Date(timestamp).getTime() : NaN;
  if (!Number.isNaN(parsedTime)) return parsedTime;
  return Number(reading?.id) || index;
};

const getLatestReading = (readings) => (
  readings.reduce((latest, reading, index) => {
    if (!latest) return { reading, score: getReadingScore(reading, index) };
    const score = getReadingScore(reading, index);
    return score > latest.score ? { reading, score } : latest;
  }, null)?.reading ?? null
);

const parseGodJson = (data) => {
  const readings = data?.ammonia?.readings ?? [];
  const latest = getLatestReading(readings);

  return {
    wetPercentage:     latest?.wet_litter_percentage ?? 0,
    ammoniaLevel:      latest?.ammonia_value_mean ?? 0,
    birdCount:         latest?.bird_count ?? 0,
    wetnessGrowthRate: latest?.wetness_growth_rate ?? latest?.advanced?.slerExpansionRate ?? 0,
    latestTimestamp:   latest?.iso8601_timestamp_string ?? null,
    nextUpdate:        data?.main?.['expected-time-next-json-update'] ?? null,
    processedVideoUrl: data?.bloblinks?.latest_processed_video ?? null,
    slerVideoUrl:      data?.bloblinks?.latest_sler_video ?? null,
  };
};

const formatTimestamp = (iso) => {
  if (!iso) return null;
  try {
    const normalized = iso.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
    return new Date(normalized).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const parseApiDate = (iso) => {
  if (!iso) return null;
  const normalized = iso
    .replace(' ', 'T')
    .replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getFilenameFromUrl = (url) => {
  if (!url) return '';
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split('/').filter(Boolean).pop() || '');
  } catch {
    const cleanUrl = url.split('?')[0].split('#')[0];
    return decodeURIComponent(cleanUrl.split('/').pop() || cleanUrl);
  }
};

const parseAmmoniaFromFilename = (filename) => {
  const match = filename?.match(/^\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}Z([^.?/#]+)/);
  if (!match) return { average: null, peak: null };

  const chunks = match[1].split('-').map((chunk) => chunk.replace(/\D/g, '')).filter(Boolean);
  if (chunks.length >= 4) {
    const average = Number(`${Number(chunks[0])}.${chunks[1]}`);
    const peak = Number(`${Number(chunks[2])}.${chunks[3]}`);
    return {
      average: Number.isNaN(average) ? null : average,
      peak: Number.isNaN(peak) ? null : peak,
    };
  }

  if (chunks.length < 2) return { average: null, peak: null };

  const average = Number(chunks[0]);
  const peak = Number(chunks[1]);

  return {
    average: Number.isNaN(average) ? null : average,
    peak: Number.isNaN(peak) ? null : peak,
  };
};

const useCountdown = (isoTarget) => {
  const [label, setLabel] = useState('');
  useEffect(() => {
    if (!isoTarget) { setLabel(''); return; }
    const tick = () => {
      const targetDate = parseApiDate(isoTarget);
      if (!targetDate) { setLabel('updating...'); return; }
      const diff = targetDate - Date.now();
      if (diff <= 0) { setLabel('updating…'); return; }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setLabel(`${mins}m ${secs < 10 ? '0' : ''}${secs}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isoTarget]);
  return label;
};

// ─── Nav Icons ────────────────────────────────────────────────────────────────

const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatTooltip = ({ text, children }) => {
  const tooltipId = useId();

  return (
    <div className="relative group focus-within:z-30" tabIndex={0} aria-describedby={tooltipId}>
      {children}
      <div
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none absolute right-full top-1/2 z-30 mr-3 w-64 -translate-y-1/2 rounded-xl border border-[#AC4242]/15 bg-[#FFF7F7] px-4 py-3 text-left font-poppins text-xs leading-relaxed text-gray-700 opacity-0 shadow-xl shadow-[#AC4242]/10 transition-all duration-200 group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100"
      >
        {text}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, unit, sub, subColor, loading, bgClass, tooltip }) => (
  <StatTooltip text={tooltip}>
    <div className={`rounded-2xl shadow-md flex items-center min-h-28 overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${bgClass}`}>
    {loading ? (
      <p className="text-sm font-poppins text-gray-400 animate-pulse px-5">Loading…</p>
    ) : (
      <>
        {/* Label */}
        <div className="flex items-center justify-center px-3 xl:px-5 flex-shrink-0 w-[44%] text-center">
          <p className="text-sm xl:text-base font-poppins font-bold text-gray-800 leading-snug break-words">{title}</p>
        </div>

        {/* Divider */}
        <div className="self-stretch w-px bg-black/20 flex-shrink-0" />

        {/* Value */}
        <div className="min-w-0 flex-1 flex flex-col items-center justify-center px-3 xl:px-5 py-3 text-center">
          <p className="font-poppins font-bold text-gray-900 leading-none text-2xl lg:text-3xl xl:text-4xl break-words">
            {value}
            <span className="text-sm lg:text-base xl:text-xl font-semibold text-gray-600 ml-1">{unit}</span>
          </p>
          {sub && (
            <p className={`text-[10px] xl:text-xs font-poppins mt-0.5 leading-snug break-words ${subColor ?? 'text-gray-500'}`}>
              {sub.includes(' — ') ? (
                <>
                  {sub.split(' — ')[0]} — <span className="font-bold">{sub.split(' — ')[1]}</span>
                </>
              ) : sub}
            </p>
          )}
        </div>
      </>
    )}
    </div>
  </StatTooltip>
);

const AmmoniaStatCard = ({ average, peak, status, statusColor, loading, bgClass, tooltip }) => (
  <StatTooltip text={tooltip}>
    <div className={`rounded-2xl shadow-md flex items-center min-h-28 overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${bgClass}`}>
    {loading ? (
      <p className="text-sm font-poppins text-gray-400 animate-pulse px-5">Loading...</p>
    ) : (
      <>
        <div className="flex items-center justify-center px-3 xl:px-4 flex-shrink-0 w-[38%] text-center">
          <p className="text-xs xl:text-sm font-poppins font-bold text-gray-800 leading-snug break-words">Ammonia Level</p>
        </div>

        <div className="self-stretch w-px bg-black/20 flex-shrink-0" />

        <div className="min-w-0 flex-1 grid grid-cols-2 divide-x divide-black/10 text-center">
          <div className="min-w-0 px-1.5 xl:px-2 py-3 flex flex-col items-center justify-center">
            <p className="text-[10px] xl:text-xs font-poppins font-semibold text-gray-500 mb-1">Average</p>
            <p className="font-poppins font-bold text-gray-900 leading-none text-lg lg:text-xl xl:text-2xl whitespace-nowrap">
              {typeof average === 'number' ? average.toFixed(2) : '-'}
              <span className="text-[10px] lg:text-xs xl:text-sm font-semibold text-gray-600 ml-0.5">ppm</span>
            </p>
          </div>
          <div className="min-w-0 px-1.5 xl:px-2 py-3 flex flex-col items-center justify-center">
            <p className="text-[10px] xl:text-xs font-poppins font-semibold text-gray-500 mb-1">Peak</p>
            <p className="font-poppins font-bold text-gray-900 leading-none text-lg lg:text-xl xl:text-2xl whitespace-nowrap">
              {typeof peak === 'number' ? peak.toFixed(2) : '-'}
              <span className="text-[10px] lg:text-xs xl:text-sm font-semibold text-gray-600 ml-0.5">ppm</span>
            </p>
          </div>
          <p className={`col-span-2 text-[10px] xl:text-xs font-poppins pb-2 leading-snug ${statusColor}`}>
            detected <span className="font-bold">{status.toUpperCase()}</span>
          </p>
        </div>
      </>
    )}
    </div>
  </StatTooltip>
);

// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();

  const [wetPercentage, setWetPercentage]         = useState(0);
  const [ammoniaLevel, setAmmoniaLevel]           = useState(0);
  const [birdCount, setBirdCount]                 = useState(0);
  const [wetnessGrowthRate, setWetnessGrowthRate] = useState(0);
  const [latestTimestamp, setLatestTimestamp]     = useState(null);
  const [nextUpdate, setNextUpdate]               = useState(null);
  const [processedVideoUrl, setProcessedVideoUrl] = useState(null);
  const [slerVideoUrl, setSlerVideoUrl]           = useState(null);
  const [showWetnessGrowthVideo, setShowWetnessGrowthVideo] = useState(false);
  const [videoError, setVideoError]               = useState(false);
  const [statsLoading, setStatsLoading]           = useState(true);
  const [fetchError, setFetchError]               = useState(false);

  // ── Camera settings from localStorage (written by Settings page) ──────────
  const [camera1Label, setCamera1Label] = useState(
    () => localStorage.getItem('camera1Label') || 'Zone 1'
  );
  const [orientation, setOrientation] = useState(
    () => localStorage.getItem('camera1Orientation') || 'Default'
  );

  // Keep in sync if user changes settings in another tab or navigates back
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'camera1Label')      setCamera1Label(e.newValue || 'Zone 1');
      if (e.key === 'camera1Orientation') setOrientation(e.newValue || 'Default');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Also refresh from localStorage whenever the component mounts / user
  // navigates back from Settings (storage event doesn't fire same-tab)
  useEffect(() => {
    setCamera1Label(localStorage.getItem('camera1Label') || 'Zone 1');
    setOrientation(localStorage.getItem('camera1Orientation') || 'Default');
  }, []);

  const videoTransform = ORIENTATION_TRANSFORMS[orientation] ?? 'none';

  const countdown = useCountdown(nextUpdate);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
  }, [navigate]);

  const fetchGodJson = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/information/getgodjson`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const parsed = parseGodJson(data);
      setWetPercentage(parsed.wetPercentage);
      setAmmoniaLevel(parsed.ammoniaLevel);
      setBirdCount(parsed.birdCount);
      setWetnessGrowthRate(parsed.wetnessGrowthRate);
      setLatestTimestamp(parsed.latestTimestamp);
      setNextUpdate(parsed.nextUpdate);
      setProcessedVideoUrl(parsed.processedVideoUrl);
      setSlerVideoUrl(parsed.slerVideoUrl);
      setFetchError(false);
    } catch (err) {
      console.warn('GodJSON fetch failed:', err.message);
      setFetchError(true);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGodJson();
    const id = setInterval(fetchGodJson, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchGodJson]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const wetStatus = getStatus(wetPercentage, WET_THRESHOLDS);
  const videoUrl = showWetnessGrowthVideo ? slerVideoUrl : processedVideoUrl;
  const ammoniaFilename = getFilenameFromUrl(processedVideoUrl || slerVideoUrl);
  const parsedAmmonia = parseAmmoniaFromFilename(ammoniaFilename);
  const averageAmmonia = typeof parsedAmmonia.average === 'number'
    ? parsedAmmonia.average
    : ammoniaLevel;
  const peakAmmonia = parsedAmmonia.peak;
  const videoErrorMessage = showWetnessGrowthVideo
    ? 'Wetness growth video is not available yet.'
    : 'Go to Cameras page to add a device.';

  useEffect(() => {
    setVideoError(false);
  }, [videoUrl]);

  const wetStatusColor =
    wetStatus === 'Safe' ? 'text-green-700' :
    wetStatus === 'Moderate' ? 'text-yellow-700' : 'text-red-700';

  const wetCardBg =
    wetStatus === 'Safe'
      ? 'bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200'
      : wetStatus === 'Moderate'
      ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200'
      : 'bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200';

  const ammoniaStatus = getStatus(peakAmmonia ?? averageAmmonia, AMMONIA_THRESHOLDS);

  const ammoniaStatusColor =
    ammoniaStatus === 'Safe' ? 'text-green-700' :
    ammoniaStatus === 'Moderate' ? 'text-yellow-700' : 'text-red-700';

  const ammoniaCardBg =
    ammoniaStatus === 'Safe'
      ? 'bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200'
      : ammoniaStatus === 'Moderate'
      ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200'
      : 'bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200';

  const growthStatus = getStatus(wetnessGrowthRate, GROWTH_THRESHOLDS);

  const growthStatusColor =
    growthStatus === 'Safe' ? 'text-green-700' :
    growthStatus === 'Moderate' ? 'text-yellow-700' : 'text-red-700';

  const growthCardBg =
    growthStatus === 'Safe'
      ? 'bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200'
      : growthStatus === 'Moderate'
      ? 'bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200'
      : 'bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200';

  const suggestedInterventions = [
    ...(wetPercentage > 30 ? [
      {
        level: 'Critical',
        title: 'Increase ventilation in Zone 1',
        metric: `Wet litter: ${wetPercentage.toFixed(1)}%`,
        insight: 'Critical moisture levels detected.',
        action: 'Increase airflow immediately.',
      },
      {
        level: 'Critical',
        title: 'Check drinkers for possible leaks',
        metric: `Wet litter: ${wetPercentage.toFixed(1)}%`,
        insight: 'High wet percentage may indicate a water source issue.',
        action: 'Inspect drinkers and nearby water lines.',
      },
      {
        level: 'Critical',
        title: 'Consider bedding replacement',
        metric: `Wet litter: ${wetPercentage.toFixed(1)}%`,
        insight: 'Litter saturation is above critical threshold.',
        action: 'Replace or top-dress affected bedding.',
      },
    ] : wetPercentage >= 25 ? [
      {
        level: 'Moderate',
        title: 'Increase ventilation in Zone 1',
        metric: `Wet litter: ${wetPercentage.toFixed(1)}%`,
        insight: 'Moderate moisture detected.',
        action: 'Improve airflow to prevent escalation.',
      },
      {
        level: 'Moderate',
        title: 'Check drinkers for possible leaks',
        metric: `Wet litter: ${wetPercentage.toFixed(1)}%`,
        insight: 'Monitor water sources as a precaution.',
        action: 'Inspect drinkers during the next zone check.',
      },
    ] : []),
    ...(averageAmmonia > 25 ? [{
      level: 'Critical',
      title: 'Ammonia Hazard',
      metric: `Sensor: NH3 ${averageAmmonia.toFixed(2)} ppm`,
      insight: 'Ammonia is above the critical 25 ppm threshold. Potential bird respiratory damage.',
      action: 'Increase ventilation immediately and inspect litter, drinkers, and airflow.',
    }] : averageAmmonia > 15 ? [{
      level: 'Moderate',
      title: 'Ammonia Alert: Rising Levels',
      metric: `Sensor: NH3 ${averageAmmonia.toFixed(2)} ppm`,
      insight: 'Ammonia is rising above the 15 ppm moderate threshold.',
      action: 'Increase airflow and monitor the next ammonia reading.',
    }] : []),
    ...(wetnessGrowthRate > 1 ? [{
      level: 'Critical',
      title: 'Rapid Leak Detected',
      metric: `SLER: ${wetnessGrowthRate.toFixed(2)}%/m`,
      insight: 'Mechanical failure, such as a broken nipple drinker.',
      action: 'Immediate physical inspection of Water Line X.',
    }] : wetnessGrowthRate > 0 ? [{
      level: 'Moderate',
      title: 'Slow Moisture Accumulation',
      metric: `SLER: ${wetnessGrowthRate.toFixed(2)}%/m`,
      insight: 'Minor spill or humidity buildup.',
      action: 'Inspect zone within 4 hours; increase fan speed.',
    }] : []),
    ...(wetPercentage > 60 ? [{
      level: 'Critical',
      title: 'High Saturation Warning',
      metric: `WAR Mean: ${wetPercentage.toFixed(1)}%`,
      insight: 'Floor is predominantly wet; high disease risk.',
      action: 'Immediate litter replacement/top-dressing.',
    }] : []),
  ];

  const navItems = [
    { label: 'Dashboard', path: '/dashboard', active: true },
    { label: 'Cameras',   path: '/cameras' },
    { label: 'History',   path: '/history' },
    { label: 'Footage',   path: '/footage' },
    { label: 'Team',      path: '/team' },
    { label: 'Profile',   path: '/profile' },
    { label: 'Settings',  path: '/settings' },
  ];

  return (
    <motion.div
      className="h-screen flex overflow-hidden bg-[#F5EFE7]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      {/* ── Sidebar ── */}
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center">
            <img src="/images/logonav.png" alt="Birdseye Logo" className="h-32 w-auto" />
          </div>

          <nav className="flex-1 px-4 space-y-1">
            {navItems.map(({ label, path, active }) => (
              <button
                key={label}
                onClick={() => navigate(path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02]
                  ${active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[label]}
                {label}
              </button>
            ))}
          </nav>

          <div className="px-4 pb-6">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200"
            >
              {navIcons.Logout}
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top Bar */}
        <nav className="bg-[#F5EFE7] border-b border-gray-300">
          <div className="px-6 py-4 flex items-center justify-between">
            <img src="/images/loginlogo.png" alt="Birdseye" className="h-12 w-auto" />
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                <svg className="w-3.5 h-3.5 text-[#AC4242]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Next update in <span className="font-semibold text-[#AC4242]">{countdown || 'waiting...'}</span>
              </div>
              <div className="text-sm font-poppins text-gray-600">
                {localStorage.getItem('userEmail')}
              </div>
            </div>
          </div>
        </nav>

        {/* Dashboard Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <h1 className="text-5xl font-poppins font-semibold text-[#AC4242] mb-8">
            Dashboard
          </h1>

          {fetchError && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl px-5 py-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm font-poppins text-red-600">
                Could not reach the server. Showing last known values — will retry in {countdown || '10m'}.
              </p>
              <button
                onClick={fetchGodJson}
                className="ml-auto text-xs font-poppins font-semibold text-red-600 hover:underline"
              >
                Retry now
              </button>
            </div>
          )}

          {/* Main Grid */}
          <div className="grid grid-cols-3 gap-6 mb-8">

            {/* ── Live Feed Panel ── */}
            <div className="col-span-2 bg-white rounded-3xl shadow-xl p-6 border border-black/[0.04] transition-shadow duration-300 hover:shadow-2xl">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-poppins font-bold">Live Feed</h2>
                <span className="flex items-center gap-1.5 text-xs font-poppins font-semibold px-3 py-1 rounded-full bg-red-100 text-red-600">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  LIVE
                </span>
              </div>

              {latestTimestamp && (
                <p className="text-xs font-poppins text-gray-400 mb-3">
                  Last capture: {formatTimestamp(latestTimestamp)}
                </p>
              )}

              {/* Video container — overflow-hidden so rotated video stays clipped */}
              <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-video">
                {videoUrl && !videoError ? (
                  <video
                    key={videoUrl}
                    src={videoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ transform: videoTransform, transition: 'transform 0.3s ease' }}
                    className="w-full h-full object-cover"
                    onError={() => setVideoError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-100">
                    <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M15 10l4.553-2.069A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                    </svg>
                    <p className="text-sm font-poppins text-gray-400">
                      {statsLoading ? 'Loading feed…' : videoError ? videoErrorMessage : 'No video available'}
                    </p>
                  </div>
                )}

                {videoUrl && !videoError && (
                  <>
                    {/* ── Dynamic camera label overlay ── */}
                    <div className="absolute top-4 left-4 bg-[#AC4242] text-white px-4 py-2 rounded-lg font-poppins text-sm">
                      {camera1Label}
                    </div>
                    {wetPercentage > 15 && (
                      <div className="absolute bottom-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full font-poppins text-sm flex items-center gap-2">
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        Wet litter detected
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-3 flex justify-end">
                <label className="inline-flex items-center gap-3 cursor-pointer">
                  <span className="text-xs font-poppins font-medium text-gray-600">
                    Enable Wetness Growth Rate View
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showWetnessGrowthVideo}
                    onChange={(e) => setShowWetnessGrowthVideo(e.target.checked)}
                    disabled={!slerVideoUrl}
                  />
                  <span className="relative h-5 w-9 flex-shrink-0 rounded-full bg-gray-300 transition-colors duration-200 peer-checked:bg-[#22c55e] peer-disabled:opacity-50 peer-disabled:cursor-not-allowed peer-checked:[&>span]:translate-x-4">
                    <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200" />
                  </span>
                </label>
              </div>
            </div>

            {/* ── Right Side Cards ── */}
            <div className="grid grid-rows-4 gap-2">
              <StatCard
                title="Bird Count"
                value={birdCount}
                unit="birds"
                sub="total across 10 frames"
                subColor="text-gray-500"
                loading={statsLoading}
                bgClass="bg-white hover:bg-gray-50"
                tooltip="Shows the estimated number of birds visible across the sampled frames, helping you spot crowding, missing birds, or activity changes."
              />
              <StatCard
                title="Wet Litter Percentage"
                value={`${wetPercentage.toFixed(1)}%`}
                unit=""
                sub={wetPercentage > 0 ? `of litter is wet — ${wetStatus.toUpperCase()}` : undefined}
                subColor={wetStatusColor}
                loading={statsLoading}
                bgClass={wetCardBg}
                tooltip="Estimates how much of the litter area appears wet, so you can catch moisture buildup before it affects comfort, hygiene, or air quality."
              />
              <AmmoniaStatCard
                average={averageAmmonia}
                peak={peakAmmonia}
                status={ammoniaStatus}
                statusColor={ammoniaStatusColor}
                loading={statsLoading}
                bgClass={ammoniaCardBg}
                tooltip="Tracks ammonia concentration in ppm. The average shows typical exposure, while the peak highlights short spikes that may need ventilation or litter action."
              />
              <StatCard
                title="Wetness Growth Rate"
                value={typeof wetnessGrowthRate === 'number' ? wetnessGrowthRate.toFixed(2) : wetnessGrowthRate}
                unit="%/m"
                sub={`detected — ${growthStatus.toUpperCase()}`}
                subColor={growthStatusColor}
                loading={statsLoading}
                bgClass={growthCardBg}
                tooltip="Measures how quickly wet litter is spreading over time. Higher values mean moisture is expanding faster and may need earlier intervention."
              />
            </div>
          </div>

          {/* ── Suggested Interventions ── */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-3xl font-poppins font-semibold">Suggested Interventions</h2>
              {suggestedInterventions.length > 0 && (
                <span className="text-xs font-poppins font-bold px-3 py-1 rounded-full bg-red-100 text-red-600 animate-pulse">
                  Action needed
                </span>
              )}
            </div>

            {statsLoading ? (
              <div className="bg-white rounded-3xl shadow-lg p-7 flex items-center gap-4 animate-pulse">
                <div className="w-10 h-10 rounded-2xl bg-gray-100 flex-shrink-0" />
                <div className="h-4 bg-gray-100 rounded w-48" />
              </div>
            ) : suggestedInterventions.length > 0 ? (
              <div className="space-y-4">
                {suggestedInterventions.map(intervention => (
                  <InterventionCard
                    key={intervention.title}
                    level={intervention.level}
                    title={intervention.title}
                    metric={intervention.metric}
                    insight={intervention.insight}
                    action={intervention.action}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-lg p-7 flex items-center gap-4">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gray-50">
                  <svg className="w-5 h-5 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-poppins font-semibold text-gray-700">
                    {wetPercentage === 0 && !fetchError ? 'Awaiting data' : 'No interventions needed'}
                  </p>
                  <p className="text-xs font-poppins text-gray-400 mt-0.5">
                    {wetPercentage === 0 && !fetchError
                      ? 'Intervention suggestions will appear once wet litter data is available.'
                      : 'Litter conditions are within acceptable range.'}
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </motion.div>
  );
};

// ─── Intervention Card ────────────────────────────────────────────────────────
const InterventionCard = ({ level, title, metric, insight, action }) => {
  const critical = level === 'Critical';

  return (
    <div className="bg-white border border-red-100 rounded-3xl shadow-lg p-6 flex items-center gap-4 transition-all duration-300 hover:border-red-200 hover:shadow-xl hover:scale-[1.02]">
      <div className={`${critical ? 'bg-red-50' : 'bg-yellow-50'} rounded-3xl p-4 flex-shrink-0`}>
        <svg className={`w-8 h-8 ${critical ? 'text-red-500' : 'text-yellow-600'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
        </svg>
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[11px] font-poppins font-bold px-2 py-0.5 rounded-full ${critical ? 'bg-red-50 text-red-600' : 'bg-yellow-50 text-yellow-700'}`}>
            {level}
          </span>
          <span className="text-xs font-poppins font-semibold text-gray-400">{metric}</span>
        </div>
        <h3 className="text-xl font-poppins font-semibold mb-1">{title}</h3>
        <p className="text-base font-poppins text-gray-700">{insight}</p>
        <p className="text-sm font-poppins font-semibold text-gray-800 mt-2">Action: {action}</p>
      </div>
    </div>
  );
};

export default Dashboard;
